import { type Decoder, fromRefinement } from 'io-ts/lib/Decoder';

import { isRight } from '../fp-ts';

export function RecordOf<T extends string | number | symbol, K>(
	domain: Decoder<unknown, T>,
	codomain: Decoder<unknown, K>
): Decoder<unknown, Record<T, K>> {
	return fromRefinement<unknown, Record<T, K>>((u): u is Record<T, K> => {
		if (typeof u !== 'object' || u === null) {
			return false;
		}

		const keys = Object.keys(u);

		return keys.every((key) => {
			// every key has to pass the decoder test
			if (!isRight(domain.decode(key))) {
				return false;
			}

			// every value has to pass the decoder test
			const value = u[key as keyof typeof u];

			return isRight(codomain.decode(value));
		});
	}, 'Record<T, K>');
}

export function makeRecordDecoder<T extends string | number | symbol, K>(
	domain: readonly T[],
	codomain: Decoder<unknown, K>
): Decoder<unknown, Record<T, K>> {
	return fromRefinement<unknown, Record<T, K>>((u): u is Record<T, K> => {
		if (typeof u !== 'object' || u === null) {
			return false;
		}

		return domain.every((key) => {
			// all the keys have to be defined
			if (!(key in u)) {
				return false;
			}

			// every value has to pass the decoder test
			const value = (u as Record<T, unknown>)[key];

			return isRight(codomain.decode(value));
		});
	}, 'Record<T, K>');
}

export function makePartialRecordDecoder<T extends string | number | symbol, K>(
	domain: readonly T[],
	codomain: Decoder<unknown, K>
): Decoder<unknown, Partial<Record<T, K>>> {
	return fromRefinement<unknown, Partial<Record<T, K>>>((u): u is Partial<Record<T, K>> => {
		if (typeof u !== 'object' || u === null) {
			return false;
		}

		const keys = Object.keys(u);

		return keys.every((key) => {
			// every key has to pass the decoder test
			if (!domain.includes(key as T)) {
				return false;
			}

			// every value has to pass the decoder test
			const value = (u as Record<T, unknown>)[key as T];

			return isRight(codomain.decode(value));
		});
	}, 'Partial<Record<T, K>>');
}
